/**
 * sibling-color-swapper.js
 *
 * Intercepts sibling colour-swatch clicks on The Sak PDP and performs
 * surgical in-place DOM swaps — no full page reload, no header/Yotpo flash.
 *
 * ── Two-phase swap strategy ────────────────────────────────────────────────
 *
 *  PHASE 1 — above-fold (instant, from prefetch cache)
 *   Fetches only api-product-color-swap.liquid — a tiny lean section.
 *   Prefetched on viewport entry + hover so it's already in memory on click.
 *   Updates: gallery · title text · price · siblings · variants · ATC ·
 *             upsell · tabs
 *
 *  PHASE 2 — below-fold (non-blocking background fetch, starts on click)
 *   Fetches the below-fold Shopify sections separately, after Phase 1 is done.
 *   Because it is NOT awaited before the main swap, Phase 1 is never delayed.
 *   Each section container is replaced quietly once the response arrives
 *   (~200-500 ms later, while the user is looking at the freshly-swapped
 *   above-fold content).
 *   Updates: 3-column text · Fastr slots · SearchSpring recommendations
 *
 * ── What is never swapped ──────────────────────────────────────────────────
 *  Yotpo stars (persists from server render), Shop Pay, TrueFit widget,
 *  sustainability icons, breadcrumbs, Yotpo media gallery, Yotpo reviews,
 *  Timeline section — all remain untouched and never flash.
 *
 * ── Transition ─────────────────────────────────────────────────────────────
 *  The gallery column and right product column each dim to 0.6 opacity
 *  as a single unit before the swap, then fade back to 1.  No per-element
 *  opacity changes → no staggered flicker.
 *
 * ── Prefetch ───────────────────────────────────────────────────────────────
 *  A. Swatches enter viewport → prefetch ALL sibling above-fold sections (low)
 *  B. Hover swatch container  → bump all to high priority
 *  C. Hover individual swatch → ensure that swatch is started
 *
 * Events dispatched on document:
 *   color-swap:start    { detail: { url } }
 *   color-swap:complete { detail: { url, productData } }
 *   color-swap:error    { detail: { url, error } }
 */

(function () {
  'use strict';

  // ─── Config ───────────────────────────────────────────────────────────────

  /** Lean Liquid section — small payload, prefetched on hover. */
  const SECTION_ID = 'api-product-color-swap';

  const BELOW_FOLD_SECTION_IDS = []; // unused — Section Rendering API returns null for all app sections

  /**
   * App-owned section IDs (Fastr, SearchSpring) that need a full-page HTML
   * fetch.  Shopify's Section Rendering API returns null for these, so we
   * fetch the entire product page as HTML and extract these sections directly.
   *
   * Fastr sections are fully server-rendered — the correct product content is
   * baked into the HTML.  The SS recs-init script contains the product ID and
   * is re-executed after swap so SearchSpring re-fetches recommendations.
   */
  /**
   * SearchSpring section IDs for full-page fetch.
   * Fastr slots are handled separately in _reinitFastr() — they are
   * client-side rendered via a pathname check so re-executing their
   * inline scripts (after pushState) is sufficient and much faster.
   */
  // All SearchSpring sections are handled by _reinitSSTabbed(), which replaces
  // the <script type="searchspring/recommendations"> in the tabbed section
  // (covering bundle, cross-sell, and recently-viewed profiles) and issues a
  // single delayed tab click.  ss_recs_script_XMcJNC was previously listed here
  // but its profiles overlap with the tabbed section script, causing SS's
  // MutationObserver to fire twice and throw duplicate null-reference errors in
  // SS's CDN code.  Keeping this empty eliminates the double-fire.
  const APP_SECTION_IDS = [];

  const SWATCH_LINK   = 'a.sibling__link';
  const CURRENT_CLASS = 'sibling__link--current';

  const FADE_OUT_MS  = 100;
  const FADE_IN_MS   = 220;
  const FADE_LEVEL   = '0.6';
  const CACHE_MAX    = 50;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function normalizePath(url) {
    try { return new URL(url, window.location.origin).pathname; }
    catch (_) { return url; }
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  function fadeEl(el, to, ms) {
    if (!el) return;
    el.style.transition = `opacity ${ms}ms ease`;
    el.style.opacity    = to;
  }

  function swapEl(oldEl, newEl) {
    if (!oldEl || !newEl) return;
    oldEl.replaceWith(newEl);
  }

  /**
   * Re-execute inline <script> tags in a newly inserted container.
   * External src= scripts are skipped to avoid double-registering handlers.
   */
  function reExecInlineScripts(container) {
    container.querySelectorAll('script:not([src])').forEach(old => {
      const s = document.createElement('script');
      if (old.type) s.type = old.type;
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }

  // ─── Above-fold zone selectors ────────────────────────────────────────────
  //
  // .product__head is intentionally NOT here — we update title text only
  // to preserve the block_style spacing that sits between the title and
  // the Yotpo stars.

  const ZONES = [
    { query: doc => doc.querySelector('product-images') },
    { query: doc => doc.querySelector('[data-price-wrapper]')?.closest('.product__block') },
    { query: doc => doc.querySelector('[data-siblings-pdp-root]') },
    { query: doc => doc.querySelector('[data-product-variants]') },
    { query: doc => doc.querySelector('.product__block--buttons') },
    { query: doc => doc.querySelector('.upsell-products') },
    { query: doc => doc.querySelector('.product__block--tabs') },
  ];

  // ─── Swapper ──────────────────────────────────────────────────────────────

  class SiblingColorSwapper {
    constructor() {
      this._aboveCache = new Map(); // path → Promise<htmlString>  (prefetched above-fold section)
      this._appCache   = new Map(); // path → Promise<Document>    (prefetched full product page)
      this._belowCache = new Map(); // unused, kept for compat
      this._swapping   = false;
      this._swymWishlistCache = null; // cached swat.fetch() result; invalidated on add/remove
      this._bindEvents();
      this._scheduleViewportPrefetch();
    }

    // ── Event binding ────────────────────────────────────────────────────────

    _bindEvents() {
      document.addEventListener('click',      this._onClick.bind(this), true);
      document.addEventListener('mouseenter', this._onContainerEnter.bind(this), true);
      document.addEventListener('mouseover',  this._onSwatchHover.bind(this), { passive: true });
      window.addEventListener('popstate',     this._onPopState.bind(this));
      document.addEventListener('color-swap:complete', this._reinitThumbScrollers.bind(this));
      document.addEventListener('color-swap:complete', ()           => { this._reinitFastr(); });
      document.addEventListener('color-swap:complete', ({ detail }) => { this._reinitSSTabbed(detail.productData); });
      document.addEventListener('color-swap:complete', ()           => { this._reinitSwym(); });
      document.addEventListener('color-swap:complete', ()           => { this._reinitPurpleDot(); });
      // Skipping Yotpo star rating update on color swap for now
      // document.addEventListener('color-swap:complete', ({ detail }) => { this._reinitYotpo(detail.url, detail.productData); });
      document.addEventListener('color-swap:complete', ({ detail }) => { this._reinitIconBlock(detail.url); });
      document.addEventListener('color-swap:complete', ({ detail }) => { this._fireAnalytics(detail.url, detail.productData, detail.doc); });
      document.addEventListener('theme:variant:change', this._onVariantChangeSwym.bind(this));
    }

    // ── Viewport prefetch (above-fold only) ──────────────────────────────────

    _scheduleViewportPrefetch() {
      const root = document.querySelector('[data-siblings-pdp-root]');
      if (!root) return;

      const observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        root.querySelectorAll(`${SWATCH_LINK}:not(.${CURRENT_CLASS})`).forEach(a => {
          this._prefetchAbove(a.href, 'low', 'viewport');
        });
      }, { threshold: 0.25 });

      observer.observe(root);
    }

    _onContainerEnter(e) {
      // SVG child elements and text nodes don't have .closest — guard first
      if (typeof e.target?.closest !== 'function') return;
      const root = e.target.closest('[data-siblings-pdp-root]');
      if (!root) return;
      root.querySelectorAll(`${SWATCH_LINK}:not(.${CURRENT_CLASS})`).forEach(a => {
        this._prefetchAbove(a.href, 'high', 'container-enter');
      });
    }

    _onSwatchHover(e) {
      if (typeof e.target?.closest !== 'function') return;
      const link = e.target.closest(SWATCH_LINK);
      if (!link || link.classList.contains(CURRENT_CLASS)) return;
      this._prefetchAbove(link.href, 'high', 'swatch-hover');
    }

    // ── Click ─────────────────────────────────────────────────────────────────

    _onClick(e) {
      if (typeof e.target?.closest !== 'function') return;
      const link = e.target.closest(SWATCH_LINK);
      if (!link) return;
      if (link.classList.contains(CURRENT_CLASS)) { e.preventDefault(); return; }
      e.preventDefault();
      e.stopPropagation();
      this._swapTo(link.href, link);
    }

    // ── Prefetch ──────────────────────────────────────────────────────────────

    _prefetchAbove(url, priority = 'high', trigger = 'unknown') {
      const path = normalizePath(url);
      if (this._aboveCache.has(path)) return;

      const isIntent = trigger === 'swatch-hover' || trigger === 'click';
      if (this._aboveCache.size >= CACHE_MAX) {
        if (!isIntent) return;
        this._aboveCache.delete(this._aboveCache.keys().next().value);
      }

      const p = this._fetchAbove(path, priority);
      p.catch(err => console.warn('[ColorSwap] prefetch failed for', path, err));
      this._aboveCache.set(path, p);
    }

    async _fetchAbove(path, priority = 'high') {
      const res  = await fetch(`${path}?sections=${SECTION_ID}`, { priority });
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${path}`);
      const json = await res.json();
      const html = json[SECTION_ID];
      if (typeof html !== 'string') throw new Error(`Section "${SECTION_ID}" missing`);
      return html;
    }

    // Fetch the full product page HTML and parse it into a Document.
    // Used to extract Fastr / SS sections that the Section Rendering API
    // cannot render (always returns null for app-owned sections).
    async _fetchAppPage(path) {
      try {
        const res  = await fetch(path, { priority: 'low' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        return new DOMParser().parseFromString(html, 'text/html');
      } catch (err) {
        console.warn('[ColorSwap] full-page prefetch failed:', err);
        return null;
      }
    }

    // ── Start below-fold background fetch (non-blocking) ─────────────────────
    // Shopify's Section Rendering API accepts max 5 section IDs per request.
    // We batch BELOW_FOLD_SECTION_IDS into chunks of 5 and fetch in parallel.

    _startBelowFetch(path) {
      if (!BELOW_FOLD_SECTION_IDS.length) return;
      if (this._belowCache.has(path)) return;

      // Split into chunks of 5 (Shopify's per-request limit)
      const CHUNK = 5;
      const chunks = [];
      for (let i = 0; i < BELOW_FOLD_SECTION_IDS.length; i += CHUNK) {
        chunks.push(BELOW_FOLD_SECTION_IDS.slice(i, i + CHUNK));
      }

      const promise = Promise.all(
        chunks.map(ids => {
          const url = `${path}?sections=${ids.join(',')}`;
          return fetch(url)
            .then(r => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              return r.json();
            })
            .catch(err => {
              console.error('[ColorSwap] below-fold chunk fetch failed:', err);
              return {}; // return empty object so other chunks still apply
            });
        })
      ).then(results => {
        // Merge all chunk results into one flat json object
        const merged = Object.assign({}, ...results);
        const found   = BELOW_FOLD_SECTION_IDS.filter(id => typeof merged[id] === 'string');
        const missing = BELOW_FOLD_SECTION_IDS.filter(id => typeof merged[id] !== 'string');
        if (missing.length) console.warn('[ColorSwap] below-fold sections missing:', missing);
        return merged;
      });

      this._belowCache.set(path, promise);
    }

    // ── Core swap ─────────────────────────────────────────────────────────────

    async _swapTo(url, clickedLink, pushState = true) {
      if (this._swapping) return;
      this._swapping = true;

      // Clean up any bisGuard observer + timer left over from the previous swap.
      // Prevents the prior timeout from disconnecting the new observer if swaps
      // happen faster than FADE_IN_MS + 100ms (e.g. if _swapping guard changes).
      clearTimeout(this._bisGuardTimer);
      if (this._bisGuard) { this._bisGuard.disconnect(); this._bisGuard = null; }

      const path = normalizePath(url);
      emit('color-swap:start', { url });

      // Kick off the below-fold fetch immediately — runs in background
      if (BELOW_FOLD_SECTION_IDS.length) this._startBelowFetch(path);

      // Optimistic swatch highlight
      if (clickedLink) {
        document.querySelectorAll('.' + CURRENT_CLASS)
          .forEach(el => el.classList.remove(CURRENT_CLASS));
        clickedLink.classList.add(CURRENT_CLASS);
      }

      // Dim parent containers as single units
      const contentEl = document.querySelector('.product__content');
      const galleryEl = document.querySelector('product-images');
      fadeEl(contentEl, FADE_LEVEL, FADE_OUT_MS);
      fadeEl(galleryEl, FADE_LEVEL, FADE_OUT_MS);

      // Hide star block before ZONES swaps + pushState to prevent visible Yotpo reinit flash.
      // Yotpo intercepts pushState (and watches DOM mutations) and calls initWidgets(),
      // which clears the widget content before re-rendering — a visible collapse.
      // We make the block invisible and lock its height first; _reinitYotpo restores both
      // after the off-screen clone has fully rendered and is atomically swapped in.
      this._starBlockEl = null;
      // const _starInner = document.querySelector('[id*="yotpo_product_reviews_star_rating"]');
      // if (_starInner) {
      //   const _starBlock = _starInner.closest('.product__block');
      //   if (_starBlock) {
      //     this._starBlockEl = _starBlock;
      //     _starBlock.style.transition = 'none';
      //     _starBlock.style.opacity    = '0';
      //     _starBlock.style.minHeight  = (_starBlock.offsetHeight || 30) + 'px';
      //     // Belt-and-suspenders: pre-mark widget instance so initWidgets() may skip it
      //     _starInner.querySelector('.yotpo-widget-instance')?.setAttribute('data-yotpo-element-loaded', '');
      //   }
      // }

      try {
        // ── PHASE 1: above-fold (instant from prefetch cache) ─────────────────
        if (!this._aboveCache.has(path)) this._prefetchAbove(url, 'high', 'click');
        const html = await this._aboveCache.get(path);
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        // 1. Product JSON first — product-form reads it on connectedCallback
        const newJson = doc.querySelector('[data-product-json]');
        const oldJson = document.querySelector('[data-product-json]');
        if (newJson && oldJson) oldJson.replaceWith(newJson);

        // 2. Final-sale metafield
        const newMeta = doc.querySelector('[data-variant-final-sale-metafield]');
        const oldMeta = document.querySelector('[data-variant-final-sale-metafield]');
        if (newMeta && oldMeta) oldMeta.replaceWith(newMeta);

        // 3. Title text only — leaves .product__head block intact so the
        //    spacing between title and Yotpo stars never shifts.
        const newCaption = doc.querySelector('[data-zoom-caption]');
        const oldCaption = document.querySelector('[data-zoom-caption]');
        if (newCaption && oldCaption) oldCaption.textContent = newCaption.textContent;

        // Pre-dim the new gallery before inserting it so the fade-in transition below
        // has something to transition FROM.  Without this the new product-images element
        // starts at opacity 1 (default), making the requestAnimationFrame fade a no-op
        // and causing the gallery to snap-bright instead of fading in smoothly.
        const newGalleryEl = doc.querySelector('product-images');
        if (newGalleryEl) newGalleryEl.style.opacity = FADE_LEVEL;

        // 15. BIS guard — MutationObserver approach.
        //
        // Problem: after swapping .product__block--buttons, product-form.connectedCallback
        // fires and calls handleVariantChange, which un-hides the BIS button if the first
        // variant by position is sold out. This happens whether the dispatch is sync or
        // deferred via setTimeout — making it impossible to reliably correct with either
        // a synchronous call or a setTimeout(0) race.
        //
        // Solution: MutationObserver fires as a microtask immediately after each DOM
        // attribute mutation, BEFORE the next task queue entry runs. So when handleVariantChange
        // removes the [hidden] attribute/class from the BIS button, the MO callback fires
        // in that same microtask checkpoint and re-hides it — always after handleVariantChange,
        // always before the next paint, invisible to the user since content is still faded.
        const self = this;
        this._bisGuard = new MutationObserver((mutations) => {
          for (const m of mutations) {
            const el = m.target;
            // Only react to the BIS button inside .product__block--buttons,
            // not any other .klaviyo-bis-trigger that might exist elsewhere.
            if (el.classList?.contains('klaviyo-bis-trigger') &&
                el.closest('.product__block--buttons')) {
              // BIS became visible — hide it (no size selected yet after color swap)
              if (!el.hasAttribute('hidden') || !el.classList.contains('hidden')) {
                self._syncBisState();
                break;
              }
            }
          }
        });
        // DO NOT observe document.body here. The ZONES swap fires ~15 DOM mutations
        // across the entire above-fold in the next line — observing body with subtree:true
        // triggers the callback on all of them, which is expensive and unnecessary.
        //
        // Observe the incoming buttons block (still detached from doc) instead.
        // MutationObservers track detached nodes; mutations that fire after insertion
        // are still caught — which is exactly when connectedCallback runs and may show BIS.
        // Falls back to body only if the buttons block is absent from the API response.
        const newButtonsBlock = doc.querySelector('.product__block--buttons');
        this._bisGuard.observe(newButtonsBlock || document.body, {
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden', 'class']
        });

        // Swym epi guard — the swap below triggers product-form's connectedCallback,
        // which fires a synthetic theme:variant:change for whatever variant its
        // internal form-state resolves to (no size is checked after a swap, so this
        // can land on an unavailable variant — see _onVariantChangeSwym for detail).
        // This flag lets _onVariantChangeSwym recognize and correct exactly that one
        // synthetic event, without touching any later, genuinely user-driven event.
        this._swymSwapPending = true;

        // 4–10. Above-fold zones (.product__block--buttons swap triggers connectedCallback)
        ZONES.forEach(zone => swapEl(zone.query(document), zone.query(doc)));

        // Synchronous pass: hides BIS if it was already visible from a prior size selection
        // (carried-over state before the new zone HTML was inserted).
        this._syncBisState();

        // 11. Three-column text section — direct DOM update via data attrs
        //     (Section Rendering API returns null for this section on the live site)
        this._updateThreeColumnSection(doc);

        // 12. Sync form__wrapper sold-out class — this wrapper div is NOT swapped,
        //     so its variant--soldout CSS class must be toggled manually to keep any
        //     CSS rules that target it (e.g. sold-out overlays) in sync.
        const formWrapper = document.querySelector('[data-form-wrapper]');
        if (formWrapper) {
          try {
            const pd = JSON.parse(document.querySelector('[data-product-json]')?.textContent);
            const available = pd?.variants?.some(v => v.available);
            formWrapper.classList.toggle('variant--soldout', !available);
          } catch (_) {}
        }

        // 13. Sync cart-bar button text — cart bar is not in ZONES so its
        //     pre-order / add-to-bag / sold-out text must be mirrored from
        //     the freshly swapped .product__block--buttons after each color swap.
        const atcTextEl     = document.querySelector('.product__block--buttons [data-add-to-cart-text]');
        const cartBarTextEl = document.querySelector('#cart-bar [data-add-to-cart-text]');
        if (atcTextEl && cartBarTextEl) cartBarTextEl.textContent = atcTextEl.textContent;

        // 14. Fix external variant-ID hidden input (ATC "items" error fix)
        this._fixExternalVariantInput();

        // 15. Re-evaluate product-sticky heights for new gallery
        const stickyEl = document.querySelector('product-sticky');
        if (stickyEl && typeof stickyEl.stickyScrollCheck === 'function') {
          stickyEl.stickyScrollCheck();
        }

        // Disconnect BIS guard after the fade-in completes.
        // Any theme JS deferred work finishes well within this window.
        // Content is still transitioning during this period so no flash is visible.
        this._bisGuardTimer = setTimeout(() => { this._bisGuard?.disconnect(); this._bisGuard = null; }, FADE_IN_MS + 100);

        // Fade back in
        requestAnimationFrame(() => {
          fadeEl(document.querySelector('.product__content'), '', FADE_IN_MS);
          fadeEl(document.querySelector('product-images'),   '', FADE_IN_MS);
        });

        // URL + document title
        if (pushState) history.pushState({ colorSwap: true, url }, '', url);
        const newTitle = doc.querySelector('title');
        if (newTitle) document.title = newTitle.textContent;

        // BIS clicks are handled by the standalone SakBisHandler (document-level
        // capture listener registered once at page load — no per-swap action needed).

        // Analytics
        let productData = null;
        try {
          const jsonEl = document.querySelector('[data-product-json]');
          if (jsonEl) productData = JSON.parse(jsonEl.textContent);
        } catch (_) {}

        // Non-blocking: swap app sections from the prefetched full page.
        this._applyAppSections(path);

        // doc is passed in payload so below-fold listeners (_reinitFastr, _reinitSSTabbed,
        // _reinitSwym, _reinitPurpleDot, _fireAnalytics, _reinitYotpo) can consume it.
        emit('color-swap:complete', { url, productData, doc });

      } catch (err) {
        fadeEl(document.querySelector('.product__content'), '', FADE_IN_MS);
        fadeEl(document.querySelector('product-images'),   '', FADE_IN_MS);
        // this._restoreStarBlock();
        emit('color-swap:error', { url, error: err });
        window.location.href = url;
        return;
      } finally {
        this._swapping = false;
      }
    }

    // ── Three-column section update ───────────────────────────────────────────
    //
    // Reads metafield values from the hidden data element we embed in
    // api-product-color-swap.liquid and surgically updates the live
    // sak-three-column section's text nodes.  This bypasses the Section
    // Rendering API, which returns null for this section on the live site.

    _updateThreeColumnSection(doc) {
      const data = doc.querySelector('[data-sak-three-col-data]');
      if (!data) return;

      // Find the live section via data attribute — set on the outer wrapper in sak-three-column.liquid.
      // Works regardless of section ID or theme ID. Falls back to shopify-section ID suffix match.
      const liveWrapper = document.querySelector('[data-sak-three-col]')?.closest('[id^="shopify-section"]')
                       || document.querySelector('[data-sak-three-col]')
                       || document.querySelector('[id$="__sak_three_column_EH8aGg"]')
                       || document.querySelector('[id*="sak_three_column"]');

      if (!liveWrapper) {
        // Section not in the template — silently skip.
        return;
      }

      // Inner wrapper with data-sak-three-col (may be liveWrapper itself if no shopify-section parent)
      const liveInner = liveWrapper.querySelector('[data-sak-three-col]') || liveWrapper;

      const visible = data.dataset.visible === 'true';
      liveInner.style.display = visible ? '' : 'none';
      if (!visible) return;

      // Stable selectors matching sak-three-column.liquid class names.
      // Falls back to [class*=] wildcards for any legacy AI-generated version.
      const columns = liveWrapper.querySelectorAll(
        '.sak-three-col__column, [class*="__column-"]'
      );
      const updates = [
        { sub: data.dataset.col1Sub, body: data.dataset.col1Body },
        { sub: data.dataset.col2Sub, body: data.dataset.col2Body },
        { sub: data.dataset.col3Sub, body: data.dataset.col3Body },
      ];

      columns.forEach((col, i) => {
        if (!updates[i]) return;
        const subEl  = col.querySelector('.sak-three-col__subheader, [class*="__subheader-"]');
        const bodyEl = col.querySelector('.sak-three-col__text, [class*="__text-"]');
        if (subEl  && updates[i].sub  != null) subEl.textContent = updates[i].sub;
        if (bodyEl) {
          const p   = bodyEl.querySelector('p');
          const val = updates[i].body ?? '';
          if (p) p.textContent = val;
          else   bodyEl.textContent = val;
        }
      });

    }

    // ── Fastr Content Slots re-init ───────────────────────────────────────────
    //
    // fastr-content-slot.liquid renders one inline <script> per block.
    // Each script reads window.location.pathname, compares it to the block's
    // configured handle list, and (on match) appends Fastr's remote init.js
    // to the slot container (#a{code}).  Since pushState has already updated
    // the pathname, we just need to:
    //   1. Clear the slot containers (so init.js renders fresh, not on top)
    //   2. Re-execute the inline scripts — they self-select the right slot

    _reinitFastr() {
      // fastr-content-slot.liquid puts a self-executing <script> INSIDE each
      // #a{code} container.  That script reads window.location.pathname and
      // (on match) appends Fastr's remote init.js to the same container.
      //
      // Problem with a naive innerHTML = '' + reExecInlineScripts approach:
      //   clearing the container removes the inline <script> itself, so there
      //   is nothing left to re-execute.
      //
      // Correct sequence per container:
      //   1. Read the inline script text before touching anything
      //   2. Clear the container (removes old rendered content + old scripts)
      //   3. Re-inject a fresh <script> with the saved text → executes with
      //      the updated pathname → Fastr loads the matching slot from CDN

      let count = 0;
      document.querySelectorAll('[id*="fastr_content_slot"]').forEach(section => {
        section.querySelectorAll('.custom__item-inner--html').forEach(container => {
          const inlineScript = container.querySelector('script:not([src])');
          if (!inlineScript) return;

          const scriptText = inlineScript.textContent;

          // Wipe rendered content AND the stale script
          container.innerHTML = '';

          // Re-inject and execute with the new pathname
          const fresh = document.createElement('script');
          fresh.textContent = scriptText;
          container.appendChild(fresh);
        });
        count++;
      });
    }

    // ── App section swap ──────────────────────────────────────────────────────

    async _applyAppSections(path) {
      if (!APP_SECTION_IDS.length) return;
      try {
        if (!this._appCache.has(path)) this._appCache.set(path, this._fetchAppPage(path));
        const newDoc = await this._appCache.get(path);
        if (!newDoc) return;
        APP_SECTION_IDS.forEach(id => {
          try {
            const newEl  = newDoc.querySelector(`[id$="__${id}"]`);
            const liveEl = document.querySelector(`[id$="__${id}"]`);
            if (!newEl || !liveEl) return;
            const cloned = newEl.cloneNode(true);
            liveEl.replaceWith(cloned);
            reExecInlineScripts(cloned);
          } catch (err) {
            console.error(`[ColorSwap] app section error (${id}):`, err);
          }
        });
      } catch (err) {
        console.error('[ColorSwap] _applyAppSections error:', err);
      }
    }

    // ── Icon block re-init ────────────────────────────────────────────────────

    async _reinitIconBlock(url) {
      const path = normalizePath(url);
      try {
        if (!this._appCache.has(path)) this._appCache.set(path, this._fetchAppPage(path));
        const newDoc = await this._appCache.get(path);
        if (!newDoc) return;
        const newEl  = newDoc.querySelector('[data-sak-icon-block]');
        const liveEl = document.querySelector('[data-sak-icon-block]');
        if (newEl && liveEl) {
          const cloned = newEl.cloneNode(true);
          liveEl.replaceWith(cloned);
          const anchor = cloned.querySelector('.product-icon-text-block a[href]');
          if (anchor && anchor.getAttribute('href') !== '#') {
            const targetSel = anchor.getAttribute('href');
            anchor.addEventListener('click', e => {
              e.preventDefault();
              const target = document.querySelector(targetSel);
              if (!target) return;
              window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
            });
          }
        }
      } catch (err) {
        console.warn('[ColorSwap] _reinitIconBlock failed:', err);
      }
    }

    // ── SearchSpring tabbed widget reinit ─────────────────────────────────────
    //
    // SS Snap exposes controllers via window.searchspring.controller (singular),
    // keyed as recommend_{tag}_{index}. We update globals.products on both
    // config and context, then call search().
    //
    // With batched: true, search() resolves when the request is *queued* — not
    // when results land. We hook afterStore (fires when store is populated) and
    // use one rAF to let Preact commit, then assign cart.items to pre-select all
    // bundle results, mirroring initial page-load state.
    //
    // recently-viewed is browser-history based — no SKU needed, excluded.

    async _reinitSSTabbed(productData) {
      try {
        const sku = productData?.variants?.[0]?.sku;
        if (!sku) return;

        const controllers = window.searchspring?.controller;
        if (!controllers) return;

        const PRODUCT_TAGS = new Set(['bundle', 'cross-sell']);

        const targets = Object.values(controllers).filter(
          c => c?.config?.tag && PRODUCT_TAGS.has(c.config.tag)
        );
        if (!targets.length) return;

        // Subscribe to afterStore BEFORE calling search() — with batched: true,
        // search() resolves when the request is queued, not when results land.
        // afterStore fires when results are actually in the store.
        // One rAF lets Preact commit before we write cart.items.
        // { once: true } prevents listener accumulation across swaps.
        const bundleCtrl = targets.find(c => c.config.tag === 'bundle');
        if (bundleCtrl) {
          bundleCtrl.eventManager.on('afterStore', ({ controller: bc }) => {
            requestAnimationFrame(() => {
              if (bc.store.results?.length) bc.store.cart.items = [...bc.store.results];
            });
          }, { once: true });
        }

        targets.forEach(c => {
          if (c.config?.globals)  c.config.globals.products  = [sku];
          if (c.context?.globals) c.context.globals.products = [sku];
          c.search();
        });

      } catch (err) {
        console.warn('[ColorSwap] _reinitSSTabbed error:', err);
      }
    }

    // ── Swym custom wishlist button ───────────────────────────────────────────
    //
    // product-buttons.liquid always renders our custom button (swym-loaded keeps
    // Swym from injecting its own). _reinitSwym() runs on page load and after
    // every color swap to wire onclick and sync initial wishlist state.
    // _onVariantChangeSwym() keeps data-swym-epi current when a size is selected.

    _reinitSwym() {
      const swat = window._swat;
      if (!swat) {
        // Swym not ready yet — queue for when it initialises.
        // Guard: only one callback in flight at a time; multiple _reinitSwym()
        // calls before Swym loads would otherwise stack callbacks that all fire
        // on init, re-wiring the button N times.
        if (!this._swymQueued) {
          this._swymQueued = true;
          window.SwymCallbacks = window.SwymCallbacks || [];
          window.SwymCallbacks.push(() => { this._swymQueued = false; this._reinitSwym(); });
        }
        return;
      }
      try {
        const btn = document.querySelector('.sak-swym-bar .swym-button[data-swym-empi]');
        if (!btn) return;

        // Snapshot non-variant fields once — empi, du, dt, iu, pr never change
        // within a color. epi is read fresh from the btn attr on every click
        // because _onVariantChangeSwym keeps it current as sizes are selected.
        // This avoids Swym's wrapCallback corruption (which only rewrites attrs,
        // not our snapshot) while still reflecting the latest selected size.
        this._swymProduct = this._swymProductFromBtn(btn);

        btn.onclick = (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          // Always read epi from the attr — _onVariantChangeSwym updates it on
          // every theme:variant:change so it reflects the selected size.
          const product = { ...this._swymProduct, epi: parseInt(btn.dataset.swymEpi, 10) };
          const isAdded  = btn.classList.contains('swym-added');
          if (isAdded) {
            btn.classList.remove('swym-added');
            swat.removeFromWishList(product,
              () => { this._swymWishlistCache = null; },
              (err) => {
                console.warn('[Swym] removeFromWishList failed — reverting', err);
                btn.classList.add('swym-added');
              }
            );
          } else {
            // uiRef.addToWishlist opens the multi-list picker and handles the
            // backend add. performAddToWishlist proxies here but has a this-binding
            // issue that silently drops the dispatch.
            const uiRef = swat.ui?.uiRef;
            if (uiRef?.addToWishlist) {
              uiRef.addToWishlist({
                product,
                successCb: () => {
                  this._swymWishlistCache = null; // wishlist changed — force refetch on next swap
                  btn.classList.add('swym-added');
                  // Swym's wrapCallback overwrites data-swym-* to its Redux store's
                  // landing product context. Restore so the next _reinitSwym() fetch
                  // check sees the correct product.
                  btn.dataset.swymEpi  = String(product.epi);
                  btn.dataset.swymEmpi = String(product.empi);
                },
                errorCb: (err) => { console.warn('[Swym] addToWishList failed', err); }
              });
            } else {
              swat.addToWishList(product,
                () => { this._swymWishlistCache = null; btn.classList.add('swym-added'); },
                (err) => { console.warn('[Swym] addToWishList failed', err); }
              );
            }
          }
        };

        // Use cached wishlist when available — avoids a network round-trip on every
        // color swap and eliminates the add→remove visual flicker on the heart icon.
        // Cache is populated on first fetch and invalidated on any add or remove.
        if (this._swymWishlistCache !== null) {
          const epi = String(this._swymProduct.epi);
          btn.classList.toggle('swym-added', this._swymWishlistCache.some(p => String(p.epi) === epi));
        } else {
          btn.classList.remove('swym-added');
          swat.fetch(
            (products) => {
              this._swymWishlistCache = products;
              const epi = String(this._swymProduct.epi);
              btn.classList.toggle('swym-added', Array.isArray(products) && products.some(p => String(p.epi) === epi));
            },
            (err) => { console.warn('[Swym] fetch error:', err); }
          );
        }

      } catch (err) { console.error('[Swym] _reinitSwym error:', err); }
    }

    _swymProductFromBtn(btn) {
      return {
        epi:  parseInt(btn.dataset.swymEpi,  10),
        empi: parseInt(btn.dataset.swymEmpi, 10),
        du:   btn.dataset.swymDu,
        dt:   btn.dataset.swymDt,
        iu:   btn.dataset.swymIu,
        pr:   parseFloat(btn.dataset.swymPr),
      };
    }

    _onVariantChangeSwym(e) {
      const variant = e?.detail?.variant;
      if (!variant) return;
      try {
        const btn = document.querySelector('.sak-swym-bar .swym-button[data-swym-empi]');
        if (!btn) return;

        // Consume the one-shot post-swap flag (see _swapTo). Only this first event
        // gets corrected — every subsequent theme:variant:change (a real size click)
        // flows through untouched below, exactly like today.
        let resolvedVariant = variant;
        if (this._swymSwapPending) {
          this._swymSwapPending = false;
          if (!variant.available) {
            try {
              const pd = JSON.parse(document.querySelector('[data-product-json]')?.textContent);
              resolvedVariant = pd?.variants?.find(v => v.available) || variant;
            } catch (_) {}
          }
        }

        btn.dataset.swymEpi = String(resolvedVariant.id);
        const swat = window._swat;
        if (!swat) return;
        btn.classList.remove('swym-added');
        swat.fetch(
          (products) => {
            btn.classList.toggle('swym-added', Array.isArray(products) && products.some(p => String(p.epi) === String(resolvedVariant.id)));
          },
          () => {}
        );
      } catch (_) {}
    }


    // ── Purple Dot pre-order re-init ─────────────────────────────────────────
    //
    // Purple Dot scopes its "hide BIS" CSS rule by adding a class like
    // `pd-style-{hash}` to a product ancestor OUTSIDE the ZONES swap area.
    // The injected rule looks like:
    //   .pd-style-{hash} .klaviyo-bis-trigger { display: none !important }
    //
    // After a color swap, that ancestor class persists — keeping BIS hidden on
    // non-preorder products. We must strip all `pd-style-*` classes before
    // calling reinit so PD starts from a clean slate on every swap.
    //
    // Must be called unconditionally on every swap in both directions:
    //   swap TO pre-order   → PD injects its waitlist UI + re-adds scope class
    //   swap AWAY from it   → PD tears down UI; scope class stripped above
    //
    // The PurpleDot v1 API reinit method name is unverified. We try the most
    // common patterns defensively. Confirm with: Object.keys(window.PurpleDot)

    _reinitPurpleDot() {
      // Strip stale PD scope classes from all ancestors (outside ZONES).
      // Without this, BIS stays hidden after swapping away from a preorder product.
      document.querySelectorAll('[class*="pd-style-"]').forEach(el => {
        [...el.classList].filter(c => /^pd-style-/.test(c)).forEach(c => el.classList.remove(c));
      });

      const pd = window.PurpleDot;
      if (!pd) return;
      try {
        if (typeof pd.reinitialize === 'function') {
          pd.reinitialize();
        } else if (typeof pd.init === 'function') {
          pd.init();
        } else if (typeof pd.load === 'function') {
          pd.load({ placementType: 'pdp' });
        }
      } catch (err) {
        console.warn('[ColorSwap] _reinitPurpleDot error:', err);
      }
    }

    // ── Yotpo star rating update via direct API ──────────────────────────────
    //
    // Strategy: fetch product ratings from Yotpo's API ourselves, then update
    // the live widget DOM directly — no initWidgets() call, no reinit flash.
    //
    // The star rating widget uses petite-vue. Score + count text live in:
    //   .yotpo-sr-bottom-line-score                — average score
    //   .yotpo-sr-bottom-line-text--right-panel    — review count
    // Stars (CSS-based) are driven by store.averageScore in the reactive scope.
    // We try accessing the Vue component internals to update the reactive store
    // (which cascades to the star icons); if unavailable we fall back to direct
    // text node updates (score + count correct, stars show stale data).
    //
    // The star block is hidden with opacity:0 + min-height in _swapTo before
    // any DOM/pushState mutations fire. We restore it in the finally block here
    // so it becomes visible again with the updated data.

    async _reinitYotpo(url, productData) {
      const liveStarEl = document.querySelector('[id*="yotpo_product_reviews_star_rating"]');
      const liveInst   = liveStarEl?.querySelector('.yotpo-widget-instance');

      // if (!liveStarEl || !liveInst || !productData?.id) {
      //   console.warn('[SAK:Yotpo] missing elements or productData — skipping');
      //   this._restoreStarBlock();
      //   return;
      // }

      // GUID lookup — cached after first call.
      // Two Yotpo loader scripts exist on this page (reviews + loyalty), so
      // document.querySelector picks whichever appears first in DOM order, which
      // may be the wrong one. Instead, look up by widget instance ID in
      // window.yotpoWidgetsContainer.guids — the instance ID is a key in the
      // widgets map for the GUID that owns it (always the reviews GUID here).
      const instanceId = liveInst.dataset?.yotpoInstanceId;
      const guid = this._yotpoGuid ?? (this._yotpoGuid = (() => {
        if (instanceId && window.yotpoWidgetsContainer?.guids) {
          const entry = Object.entries(window.yotpoWidgetsContainer.guids)
            .find(([, cfg]) => instanceId in (cfg?.config?.widgets ?? {}));
          if (entry) return entry[0];
        }
        // Fallback: first matching loader script tag
        return document.querySelector('script[src*="cdn-widgetsrepository.yotpo.com/v1/loader/"]')
          ?.src?.match(/loader\/([^?&]+)/)?.[1] ?? null;
      })());

      // Keep Yotpo's own pushState reinit from touching this element.
      // data-yotpo-element-loaded signals initWidgets() to skip it.
      liveInst.setAttribute('data-yotpo-element-loaded', '');

      try {
        if (!guid) throw new Error('GUID not found on page');

        const res = await fetch(
          `https://api-cdn.yotpo.com/v3/storefront/store/${guid}/product/${productData.id}/ratings`,
          { priority: 'high' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        // v3 API response: try several known shapes
        const bl    = json?.response?.bottomline ?? json?.bottomline ?? json?.ratings ?? json;
        const score = parseFloat(bl?.average_score ?? bl?.averageScore ?? 0);
        const count = parseInt(bl?.total_reviews ?? bl?.totalReviews ?? 0, 10);

        if (!score) throw new Error('no score in response');

        // Display rounds to 1 decimal (e.g. 4.791304 → 4.8). Compare on that
        // rounded value so a swap between products with the same displayed score
        // and count is a no-op — no flash, no unnecessary DOM mutations.
        const displayScore = parseFloat(score.toFixed(1));
        const prev = this._yotpoDisplayCache;

        // Update data attribute to keep in sync for future interactions
        liveInst.setAttribute('data-yotpo-product-id', String(productData.id));

        if (prev && prev.score === displayScore && prev.count === count) {
          return; // _restoreStarBlock fires in finally
        }

        this._yotpoDisplayCache = { score: displayScore, count };

        // ── Attempt 1: update petite-vue reactive store ───────────────────────
        // If accessible, changing store.averageScore cascades to the star icons.
        // petite-vue (Vue 3 underneath) may expose component context on elements.
        let reactiveUpdated = false;
        const candidates = [liveInst, ...liveInst.querySelectorAll('[v-scope]')];
        for (const el of candidates) {
          const proxy = el.__vueParentComponent?.proxy
                     ?? el.__vueParentComponent?.ctx;
          const store = proxy?.store;
          if (store && ('averageScore' in store || 'average_score' in store)) {
            const scoreKey = 'averageScore' in store ? 'averageScore' : 'average_score';
            const countKey = 'totalReviews' in store ? 'totalReviews' : 'total_reviews';
            store[scoreKey] = displayScore;
            if (countKey in store) store[countKey] = count;
            reactiveUpdated = true;
            break;
          }
        }

        if (!reactiveUpdated) {
          // ── Attempt 2: direct DOM text node update ────────────────────────
          // Stars will remain stale (petite-vue controls them) until the next
          // full page load or Yotpo reinit. Score + count text are updated.
          const scoreEl = liveInst.querySelector('.yotpo-sr-bottom-line-score');
          const textEl  = liveInst.querySelector('.yotpo-sr-bottom-line-text--right-panel');
          if (scoreEl) scoreEl.textContent = displayScore;
          if (textEl)  textEl.textContent  = `${count.toLocaleString()} Reviews`;
        }

      } catch (err) {
        console.warn('[SAK:Yotpo] ratings update failed:', err);
      } finally {
        // this._restoreStarBlock();
      }
    }

    // _restoreStarBlock() {
    //   if (this._starBlockEl) {
    //     this._starBlockEl.style.transition = '';
    //     this._starBlockEl.style.opacity    = '';
    //     this._starBlockEl.style.minHeight  = '';
    //     this._starBlockEl = null;
    //   }
    // }

    // ── Phase 2: swap below-fold section containers ───────────────────────────
    //
    // Each section is a #shopify-section-{id} div. We replace the live node
    // with the freshly rendered HTML for the new product, then re-execute any
    // inline scripts so SearchSpring / Fastr re-initialise.

    _applyBelowFoldSwaps(json) {
      BELOW_FOLD_SECTION_IDS.forEach(id => {
        try {
          const html = json[id];
          if (typeof html !== 'string') return; // section not in response

          // Find the live container.
          // Shopify wraps sections in <div id="shopify-section-{id}"> BUT when
          // sections come from a JSON template the ID is prefixed:
          //   shopify-section-template--{templateId}__{sectionId}
          // We try the exact ID first, then fall back to a suffix match.
          const liveEl = document.getElementById(`shopify-section-${id}`)
                      || document.querySelector(`[id$="__${id}"]`);
          if (!liveEl) {
            console.warn(`[ColorSwap] live section not found in DOM: ${id}`);
            return;
          }

          const parsed = new DOMParser().parseFromString(html, 'text/html');

          // Try the standard wrapper first; fall back to the first child of
          // <body> in case the API returns the inner HTML without the wrapper.
          const newEl = parsed.getElementById(`shopify-section-${id}`)
                     || parsed.body?.firstElementChild;
          if (!newEl) {
            console.warn(`[ColorSwap] no replacement element found for: ${id}`);
            return;
          }

          liveEl.replaceWith(newEl);
          reExecInlineScripts(newEl);

        } catch (err) {
          // Isolate failures so one broken section doesn't stop the rest.
          console.error(`[ColorSwap] error swapping section ${id}:`, err);
        }
      });
    }

    // ── Fix external variant-ID hidden input ──────────────────────────────────
    //
    // product.liquid renders input[name="id"] outside .product__block--buttons
    // with form="{old_form_id}". After the swap the new product-form has a
    // different ID → variant ID missing from cart POST → "items" error.

    _fixExternalVariantInput() {
      const idInput = document.querySelector('.form__width > input[name="id"]');
      if (!idInput) return;
      const newForm = document.querySelector('form[data-product-form]');
      if (newForm?.id) idInput.setAttribute('form', newForm.id);
      try {
        const data      = JSON.parse(document.querySelector('[data-product-json]')?.textContent);
        const variantId = data?.selected_or_first_available_variant?.id ?? data?.variants?.[0]?.id;
        if (variantId) idInput.value = String(variantId);
      } catch (_) {}

    }

    // ── BIS reset after color swap ────────────────────────────────────────────

    _syncBisState() {
      // After a color swap on a size-option product, product-form.connectedCallback
      // calls getFormState() which returns the FIRST variant by array position (not
      // first available). If that variant is sold out, handleVariantChange (and/or
      // Klaviyo onsite.js) shows BIS — even though no size has been deliberately
      // selected yet, leaving both ATC and BIS visible simultaneously.
      //
      // IMPORTANT: query within .product__block--buttons, NOT document-level.
      // The cart bar also has [data-add-to-cart] buttons that do NOT carry
      // data-has-size-option. A document-level querySelector finds the cart bar
      // button first (depending on DOM order), the has-size-option check fails,
      // and we return early without ever hiding BIS.
      try {
        const block = document.querySelector('.product__block--buttons');
        if (!block) return;
        const atcBtn = block.querySelector('[data-add-to-cart]');
        if (!atcBtn || !atcBtn.hasAttribute('data-has-size-option')) return;
        const bisBtn = block.querySelector('.klaviyo-bis-trigger');
        if (!bisBtn) return;
        bisBtn.setAttribute('disabled', '');
        bisBtn.setAttribute('hidden', '');
        bisBtn.classList.add('hidden');
      } catch (_) {}
    }

    // ── Popstate (back / forward) ─────────────────────────────────────────────

    _onPopState(e) {
      if (e.state && e.state.colorSwap) {
        this._swapTo(e.state.url, null, false);
      } else if (e.state === null) {
        window.location.reload();
      }
    }

    // ── Re-init thumb scrollers after gallery swap ────────────────────────────

    _reinitThumbScrollers() {
      // DO NOT skip disconnecting old observers before creating new ones.
      // product-images is fully replaced on each swap (old elements leave the DOM),
      // but IntersectionObservers keep their observed elements alive until disconnected.
      // Without this cleanup, each swap leaks the previous gallery's slides + closures.
      if (this._thumbObservers) {
        this._thumbObservers.forEach(obs => obs.disconnect());
      }
      this._thumbObservers = [];

      if (this._thumbClickHandlers) {
        this._thumbClickHandlers.forEach(({ thumb, handler }) => thumb.removeEventListener('click', handler));
      }
      this._thumbClickHandlers = [];

      const thumbs = document.querySelectorAll(
        '.product__images--thumbnails-left-stacked .product__thumb'
      );
      thumbs.forEach(thumb => {
        const mediaId = thumb.querySelector('a')?.getAttribute('data-media-id');
        if (!mediaId) return;
        const slide = document.querySelector(`.product__slide[data-media-id="${mediaId}"]`);
        if (!slide) return;

        const handler = () => {
          const top = slide.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        };
        thumb.addEventListener('click', handler);
        this._thumbClickHandlers.push({ thumb, handler });

        const obs = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.75) {
              if (!thumb.classList.contains('is-active')) {
                thumbs.forEach(t => t.classList.remove('is-active'));
                thumb.classList.add('is-active');
              }
            }
          });
        }, { threshold: 0.75 });
        obs.observe(slide);
        this._thumbObservers.push(obs);
      });
    }

    // ── Analytics ─────────────────────────────────────────────────────────────

    _fireAnalytics(url, productData, doc) {
      const title = document.title;

      try {
        if (typeof gtag === 'function') {
          gtag('event', 'page_view', { page_location: url, page_title: title });
          if (productData) {
            const v = productData.variants?.[0];
            gtag('event', 'view_item', {
              currency: window.Shopify?.currency?.active || 'USD',
              value:    v ? v.price / 100 : undefined,
              items:  [{ item_id: String(productData.id), item_name: productData.title, price: v ? v.price / 100 : undefined }],
            });
          }
        }
      } catch (_) {}

      try {
        if (window.klaviyo) {
          // Prefer the full payload pre-built in api-product-color-swap.liquid
          // ([data-klaviyo-viewed-product]) — it mirrors the page-load Viewed
          // Product event field-for-field, including metafield-driven fields
          // (Name, Flavor, Color, Colorway, Material, Size) that are NOT present
          // in [data-product-json]. See that section's comment + context file.
          let item = null;
          try {
            const raw = doc?.querySelector('[data-klaviyo-viewed-product]')?.textContent;
            if (raw) item = JSON.parse(raw);
          } catch (_) {}

          // Fallback to a minimal payload from product JSON if the pre-built
          // block is missing (e.g. stale cached section). Never regress to no
          // tracking.
          if (!item && productData) {
            const v = productData.selected_or_first_available_variant || productData.variants?.[0];
            item = {
              Name:      productData.title,
              ProductID: productData.id,
              VariantID: v?.id,
              SKU:       v?.sku,
              Price:     v ? v.price / 100 : undefined,
              URL:       url,
              ImageURL:  productData.featured_image ? 'https:' + productData.featured_image : undefined,
            };
          }

          if (item) window.klaviyo.push(['track', 'Viewed Product', item]);
        }
      } catch (_) {}

      try {
        if (window.searchspring?.track) {
          window.searchspring.track('product.view', { sku: productData?.variants?.[0]?.sku });
        }
      } catch (_) {}
    }
  }

  // ─── BIS click interceptor ────────────────────────────────────────────────
  //
  // DO NOT attach this listener to .product__block--buttons or any child.
  // That element is fully replaced by swapEl() on every color swap — listeners
  // on it or its children are orphaned immediately after Phase 1 completes.
  // Document-level capture (registered once at bootstrap) is never orphaned.
  // Validated 2026-05. See .claude/context/third-party.md → Klaviyo BIS.
  //
  // DO NOT call klaviyo.enable("backinstock") to restore BIS after a swap.
  // It is a no-op after the first call; variants_by_id is cached from the
  // original product. Script re-inject breaks due to Unveild patching fetch.
  // Validated 2026-05. See .claude/context/third-party.md → Klaviyo BIS.

  // ─── Bootstrap ────────────────────────────────────────────────────────────
  // SakBisHandler is loaded separately via sak-bis-handler.js (theme.liquid)
  // so BIS works even when color swap is disabled in the section settings.

  function init() {
    if (!document.querySelector('product-component[data-section-type="product"]')) return;
    window._siblingColorSwapper = new SiblingColorSwapper();
    window._siblingColorSwapper._reinitSwym();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
